import { useCallback, useEffect, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Camera, CheckCircle2, Copy, ExternalLink, Loader2, Search, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDateTime, idemKey, money } from "@/lib/api";
import { ErrorBox, Field, PageHead, StatusBadge } from "@/components/common";
import { Scanner } from "@/pages/Register";

const EMPTY = { client_name: "", client_email: "", client_mobile: "" };

function PayPanel({ sale, onChange }) {
  useEffect(() => {
    if (sale.status !== "PENDING") return undefined;
    const t = setInterval(async () => {
      try { const { data } = await api.get(`/sales/sales/${sale.id}`); if (data.status !== "PENDING") onChange(data); } catch { /* keep polling */ }
    }, 5000);
    return () => clearInterval(t);
  }, [sale, onChange]);
  const cancel = async () => {
    if (!window.confirm("Cancel this checkout and release the card?")) return;
    try { await api.post(`/sales/sales/${sale.id}/cancel`); toast.success("Checkout cancelled · card released"); onChange({ ...sale, status: "CANCELLED" }); } catch (e) { toast.error(errMsg(e)); }
  };
  if (sale.status === "PAID") {
    return (
      <div className="space-y-2 rounded-2xl border border-emerald-200 bg-emerald-50 p-5" data-testid="sale-paid">
        <p className="flex items-center gap-2 text-lg font-bold text-emerald-800"><CheckCircle2 size={20} /> Payment verified · card activated</p>
        <p className="text-sm">Receipt <b data-testid="sale-receipt-number">{sale.receipt?.number}</b> · PayMongo <span className="font-mono text-xs" data-testid="sale-paymongo-id">{sale.paymongo_payment_id}</span></p>
        <p className="text-sm">{sale.package_name} · {money(sale.amount)} · {fmtDateTime(sale.paid_at)}</p>
        <p className="text-xs text-slate-600">Hand over card <b className="font-mono">{sale.card_serial}</b>. The client registers it at “Scan your card · Register” using <b>{sale.purchaser?.email}</b>.</p>
      </div>
    );
  }
  if (sale.status !== "PENDING") return <p className="flex items-center gap-2 text-sm" data-testid="sale-closed"><StatusBadge status={sale.status} /> This checkout is closed. The card is available again.</p>;
  return (
    <div className="space-y-4 text-center" data-testid="sale-pending">
      <p className="text-sm text-slate-600">Ask the client to scan this code, or send them the link. They pay on PayMongo's secure page.</p>
      <div className="mx-auto w-fit rounded-3xl bg-white p-4 shadow-xl ring-1 ring-slate-100"><QRCodeCanvas value={sale.checkout_url} size={240} data-testid="checkout-qr" /></div>
      <p className="text-2xl font-extrabold">{money(sale.amount)}</p>
      <div className="flex flex-wrap justify-center gap-2">
        <a href={sale.checkout_url} target="_blank" rel="noreferrer" className="btn-secondary btn-sm" data-testid="open-checkout-link"><ExternalLink size={13} /> Open checkout</a>
        <button className="btn-secondary btn-sm" onClick={() => { navigator.clipboard?.writeText(sale.checkout_url); toast.success("Link copied"); }} data-testid="copy-checkout-link"><Copy size={13} /> Copy link</button>
      </div>
      <p className="flex items-center justify-center gap-2 text-sm"><Loader2 size={14} className="animate-spin" /> Waiting for PayMongo verification… <span className="font-mono text-xs text-slate-400">{sale.reference}</span></p>
      <button className="btn-danger btn-sm" onClick={cancel} data-testid="cancel-sale-button"><XCircle size={13} /> Cancel & release card</button>
    </div>
  );
}

function ClientForm({ card, onStarted }) {
  const [f, setF] = useState(EMPTY);
  const [key] = useState(idemKey);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const ok = f.client_name.length > 1 && /\S+@\S+\.\S+/.test(f.client_email) && f.client_mobile.length >= 7;
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/sales/sales", { ...f, card_serial: card.serial, idempotency_key: key });
      if (data.duplicate_flag) toast.warning("This email or mobile already has an active membership — flagged for review.");
      onStarted(data);
    } catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="client-form">
      <Field label="Client full name"><input className="field" value={f.client_name} onChange={set("client_name")} data-testid="client-name-input" /></Field>
      <Field label="Client email" hint="The client verifies this email when registering the card"><input type="email" className="field" value={f.client_email} onChange={set("client_email")} data-testid="client-email-input" /></Field>
      <Field label="Mobile"><input className="field" inputMode="tel" value={f.client_mobile} onChange={set("client_mobile")} data-testid="client-mobile-input" /></Field>
      <ErrorBox error={error} />
      <button className="btn-primary w-full py-4 text-base" disabled={!ok || busy} data-testid="create-checkout-button">{busy && <Loader2 size={16} className="animate-spin" />} Create PayMongo checkout · {money(card.package?.price)}</button>
    </form>
  );
}

export default function NewSale() {
  const [serial, setSerial] = useState("");
  const [scan, setScan] = useState(false);
  const [card, setCard] = useState(null);
  const [sale, setSale] = useState(null);
  const [error, setError] = useState("");
  const lookup = async (s) => {
    setError(""); setSale(null);
    try { const { data } = await api.get(`/sales/cards/${encodeURIComponent(s.trim())}`); setCard(data); if (data.pending_sale) setSale(data.pending_sale); }
    catch (e) { setCard(null); setError(errMsg(e)); }
  };
  const onScan = useCallback((text) => { setScan(false); const s = text.trim().split("/").pop(); setSerial(s); lookup(s); }, []);
  return (
    <div className="mx-auto max-w-xl space-y-5">
      <PageHead eyebrow="Sales Force" title="New sale" sub="PayMongo only. The card activates automatically once PayMongo confirms payment." />
      <form onSubmit={(e) => { e.preventDefault(); lookup(serial); }} className="surface space-y-3 p-4">
        <div className="flex gap-2">
          <div className="relative flex-1"><Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input className="field py-3 pl-9 font-mono uppercase" placeholder="Card serial" value={serial} onChange={(e) => setSerial(e.target.value)} required data-testid="sale-serial-input" /></div>
          <button type="button" className="btn-secondary" onClick={() => setScan((s) => !s)} data-testid="scan-card-button"><Camera size={16} /></button>
          <button className="btn-primary" data-testid="sale-lookup-button">Check</button>
        </div>
        {scan && <Scanner onResult={onScan} />}
      </form>
      <ErrorBox error={error} />
      {card && (
        <div className="surface space-y-5 p-5" data-testid="sale-card-result">
          <div className="flex items-center justify-between gap-3">
            <div><p className="font-mono text-lg font-bold">{card.serial}</p><p className="text-sm text-slate-500">{card.package?.name} · {money(card.package?.price)}</p></div>
            <StatusBadge status={card.status} testId="sale-card-status" />
          </div>
          {sale ? <PayPanel sale={sale} onChange={setSale} />
            : card.status === "AVAILABLE" ? <ClientForm card={card} onStarted={setSale} />
              : <p className="text-sm text-slate-500" data-testid="sale-card-unavailable">This card is {card.status.replace("_", " ").toLowerCase()} and cannot be sold{card.status === "PAYMENT_PENDING" ? " (pending checkout by another seller)" : ""}.</p>}
        </div>
      )}
    </div>
  );
}
